import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import UndoIcon from "@mui/icons-material/Undo";
import {
  FormHelperTextProps,
  IconButtonProps,
  InputBaseComponentProps,
  InputLabelProps,
  OutlinedInputProps,
} from "@mui/material";
import { green } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import { alpha, styled } from "@mui/material/styles";
import TextField, { OutlinedTextFieldProps } from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker, DatePickerProps, PickersInputProps } from "@mui/x-date-pickers";
import { PickerDay, pickerDayClasses, PickerDayProps } from "@mui/x-date-pickers/PickerDay";
import { PickersTextField, PickersTextFieldProps } from "@mui/x-date-pickers/PickersTextField";
import { PickerValue } from "@mui/x-date-pickers/internals";
import AHDate from "_shared/dates/date";
import { diffLines, diffWords } from "diff";
import { enqueueSnackbar } from "notistack";
import React, { forwardRef, useCallback, useEffect } from "react";
import { ProposalIssue } from "work/proposals/issues/proposal-issues";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalIncomingChangeHighlight from "work/proposals/negotiation/proposal-incoming-change-highlight";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import TextChange, { TextChangeCollection } from "work/proposals/redlining/__diff/text-change";
import { ChangeType } from "work/proposals/redlining/__diff/_views/diff-changes";
import DiffText from "work/proposals/redlining/__diff/_views/diff-text";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";

const DiffTextContainer = styled("div")(({ theme }) => ({
  flexDirection: "column",
  height: "100%",
  paddingRight: theme.spacing(2),
  // Anchors the transient incoming-change tint to this field's value; positioning alone changes no
  // geometry, so the field lays out exactly as it did before.
  position: "relative",
  width: "100%",
}));
const DiffInputContainer = styled("div")(() => ({
  width: "100%",
  "&.Mui-disabled": {
    webkitTextFillColor: "revert !important",
    opacity: "0.75 !important",
  },
}));
const DiffInputSpan = styled("span")(() => ({
  "&.MuiInputBase-input": {
    flexDirection: "column",
  },
}));
const ResolvedDateValue = styled("span")(() => ({
  color: "inherit",
  fontWeight: "inherit",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}));
const RedlineDatePicker = styled(DatePicker, {
  shouldForwardProp: (props) => props !== "traversable",
})<{ traversable?: boolean }>(() => ({
  minWidth: "20rem",
  width: "min-content",
}));
const CalendarDay = styled(PickerDay, {
  shouldForwardProp: (prop) => !["resolved", "changeType", "acceptedResolved"].includes(prop.toString()),
})<{ changeType?: ChangeType; resolved: boolean; acceptedResolved: boolean }>(({ changeType, resolved, acceptedResolved, theme }) => {
  // Days that carry no redline keep MUI's own selected/today styling — only added and removed
  // days are restyled, so an unchanged day the picker happens to have selected still highlights.
  if (changeType !== ChangeType.added && changeType !== ChangeType.removed) return {};

  return {
    [`&.${pickerDayClasses.root}`]: {
      "&:disabled": {
        color:
          changeType === ChangeType.removed && acceptedResolved
            ? theme.palette.text.disabled
            : theme.palette.text.primary,
      },
      backgroundColor: (function (): string {
        if (changeType === ChangeType.added && acceptedResolved) return "transparent";
        if (changeType === ChangeType.added && resolved) return theme.palette.primary.main;
        if (changeType === ChangeType.added) return green[300];
        return "unset";
      })(),
      border: (function (): string {
        if (changeType === ChangeType.removed) {
          let borderColor: string;
          if (acceptedResolved) {
            borderColor = theme.palette.text.primary;
          } else if (resolved) {
            borderColor = theme.palette.primary.main;
          } else {
            borderColor = theme.palette.error.main;
          }
          return `2px solid ${borderColor}`;
        }
        return "unset";
      })(),
      fontWeight: (function (): number | string {
        if (changeType === ChangeType.added && acceptedResolved) return 500;
        return "inherit";
      })(),
      fontSynthesis: (function (): string | undefined {
        if (changeType === ChangeType.added && acceptedResolved) return "weight";
        return undefined;
      })(),
      textDecoration: (function (): string {
        if (changeType === ChangeType.removed) return "line-through";
        return "unset";
      })(),
      textDecorationColor: (function (): string {
        if (changeType === ChangeType.removed) {
          let color: string;
          if (acceptedResolved) {
            color = theme.palette.text.primary;
          } else if (resolved) {
            color = theme.palette.primary.main;
          } else {
            color = theme.palette.error.main;
          }
          return color;
        }
        return "unset";
      })(),
      textDecorationThickness: (function (): string {
        if (changeType === ChangeType.removed) return "2px";
        return "unset";
      })(),
      [`&.${pickerDayClasses.selected}`]: {
        backgroundColor: (function (): string {
          if (changeType === ChangeType.added && acceptedResolved) return "transparent";
          if (changeType === ChangeType.added && resolved) return theme.palette.primary.main;
          if (changeType === ChangeType.added) return green[300];
          return "unset";
        })(),
        color: (function (): string {
          if (changeType === ChangeType.removed && acceptedResolved) return theme.palette.text.disabled;
          return theme.palette.text.primary;
        })(),
      },
      [`&.${pickerDayClasses.selected}:hover`]: {
        backgroundColor: (function (): string {
          if (changeType === ChangeType.added && acceptedResolved) return "transparent";
          if (changeType === ChangeType.added && resolved) return theme.palette.primary.main;
          if (changeType === ChangeType.added) return green[300];
          return "unset";
        })(),
      },
    },
  };
});
const ActionButton = styled(IconButton)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(0.5),
}));
const openPickerButtonSelector = "[data-redline-open-picker-button='true']";

interface RedlinePickerDayProps extends PickerDayProps {
  originalDate?: AHDate | null;
  revisedDate?: AHDate | null;
  currentDate?: AHDate | null;
  resolved?: boolean;
  acceptedResolved?: boolean;
}

export function getCalendarDayChangeType(params: {
  day: AHDate;
  originalDate: AHDate | null;
  revisedDate: AHDate | null;
  currentDate: AHDate | null | undefined;
}): ChangeType {
  const { day, originalDate, revisedDate, currentDate } = params;

  let previousDate: AHDate | null = null;
  let nextDate: AHDate | null = null;

  if ((currentDate === null && revisedDate === null) || currentDate?.isEqualTo(revisedDate)) {
    previousDate = originalDate;
    nextDate = currentDate ?? null;
  } else if (currentDate || currentDate === null) {
    previousDate = revisedDate;
    nextDate = currentDate;
  } else {
    previousDate = originalDate;
    nextDate = revisedDate;
  }

  const matchesPrevious = previousDate?.isSame(day, "day") ?? false;
  const matchesNext = nextDate?.isSame(day, "day") ?? false;

  if (matchesPrevious && matchesNext) return ChangeType.unchanged;
  if (matchesNext) return ChangeType.added;
  if (matchesPrevious) return ChangeType.removed;
  return ChangeType.unchanged;
}

export function getDisplayedDateValue(dateRedline: FieldRedline<AHDate>) {
  return dateRedline.currentEntry?.asMoment ?? dateRedline.revisedEntry?.asMoment ?? dateRedline.originalEntry?.asMoment;
}

export function shouldCommitPickerDateChange(context: { source: string; validationError: unknown }): boolean {
  return context.source === "view" && !context.validationError;
}

function RevisedDay(props: Readonly<RedlinePickerDayProps>) {
  const { originalDate, revisedDate, currentDate, resolved, acceptedResolved, ...otherProps } = props;

  let changeType = ChangeType.unchanged;

  if (!otherProps.outsideCurrentMonth) {
    changeType = getCalendarDayChangeType({
      day: AHDate.fromMoment(otherProps.day),
      originalDate: originalDate ?? null,
      revisedDate: revisedDate ?? null,
      currentDate,
    });
  }

  return (
    <CalendarDay
      {...otherProps}
      disabled={otherProps.disabled || changeType !== ChangeType.unchanged}
      selected={otherProps.selected || changeType !== ChangeType.unchanged}
      changeType={changeType}
      resolved={Boolean(resolved)}
      acceptedResolved={Boolean(acceptedResolved)}
    />
  );
}

interface DatePickerTextFieldProps extends PickersTextFieldProps<"outlined"> {
  InputProps?: Partial<OutlinedInputProps & PickersInputProps>;
  inputProps?: React.ComponentPropsWithRef<"input">;
  InputLabelProps?: Partial<InputLabelProps>;
  FormHelperTextProps?: Partial<FormHelperTextProps>;
  dateRedline?: FieldRedline<AHDate>;
  focused?: boolean;
  animateAcceptedResolved?: boolean;
  lockAdornment?: React.ReactNode;
  negotiationId?: string;
  onManualDateAccept?: () => void;
  onTraverseIn?: () => void;
}

const DatePickerTextField: React.FC<DatePickerTextFieldProps> = (props) => {
  const {
    dateRedline,
    focused,
    InputProps,
    inputProps: legacyHtmlInputProps,
    InputLabelProps,
    FormHelperTextProps,
    onTraverseIn,
    onManualDateAccept,
    animateAcceptedResolved,
    lockAdornment,
    negotiationId,
    sectionListRef,
    elements,
    areAllSectionsEmpty,
    contentEditable,
    tabIndex,
    value,
    onChange,
    onInput,
    onKeyDown,
    onKeyUp,
    onPaste,
    endAdornment,
    startAdornment,
    ...otherProps
  } = props;
  const [textEditingState, setTextEditingState] = React.useState<{
    dateRedline: FieldRedline<AHDate> | undefined;
    isEditingTextValue: boolean;
  }>({ dateRedline, isEditingTextValue: false });
  const skipStartEditingTextValueRef = React.useRef(false);
  const existingSlotProps = otherProps.slotProps;
  const existingInputSlotProps =
    existingSlotProps?.input && typeof existingSlotProps.input === "object"
      ? existingSlotProps.input
      : {};
  const existingInputLabelSlotProps =
    existingSlotProps?.inputLabel && typeof existingSlotProps.inputLabel === "object"
      ? existingSlotProps.inputLabel
      : {};
  const inputLabelSlotProps = {
    ...InputLabelProps,
    ...existingInputLabelSlotProps,
  };
  const existingFormHelperTextSlotProps =
    existingSlotProps?.formHelperText && typeof existingSlotProps.formHelperText === "object"
      ? existingSlotProps.formHelperText
      : {};
  const formHelperTextSlotProps = {
    ...FormHelperTextProps,
    ...existingFormHelperTextSlotProps,
  };
  const existingHtmlInputSlotProps =
    existingSlotProps?.htmlInput && typeof existingSlotProps.htmlInput === "object"
      ? existingSlotProps.htmlInput
      : {};
  const htmlInputSlotProps = {
    ...legacyHtmlInputProps,
    ...InputProps?.inputProps,
    ...existingHtmlInputSlotProps,
  };
  if (dateRedline === undefined) {
    return (
      <PickersTextField
        {...otherProps}
        sectionListRef={sectionListRef}
        elements={elements}
        areAllSectionsEmpty={areAllSectionsEmpty}
        contentEditable={contentEditable}
        tabIndex={tabIndex}
        value={value}
        onChange={onChange}
        onInput={onInput}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onPaste={onPaste}
        endAdornment={InputProps?.endAdornment ?? endAdornment}
        startAdornment={InputProps?.startAdornment ?? startAdornment}
        slotProps={{
          ...existingSlotProps,
          htmlInput: htmlInputSlotProps,
          inputLabel: inputLabelSlotProps,
          formHelperText: formHelperTextSlotProps,
        }}
      />
    );
  }
  const isEditingTextValue = textEditingState.dateRedline === dateRedline && textEditingState.isEditingTextValue;
  const hasAcceptedResolvedChange = dateRedline.isResolved && dateRedline.isAccepted && dateRedline.wasRevised;
  const shouldShrinkLabel = Boolean(
    dateRedline.currentEntry !== undefined &&
      dateRedline.currentEntry !== null ||
      dateRedline.originalEntry !== null ||
      dateRedline.revisedEntry !== null,
  );
  const resolvedHelperText = hasAcceptedResolvedChange ? "Resolved" : otherProps.helperText;
  const canEditTextValue = !otherProps.disabled && !InputProps?.readOnly;
  const lockedFieldSx = lockAdornment
    ? {
        "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
          transform: "translate(48px, 16px) scale(1)",
        },
        "& .MuiInputLabel-root.MuiInputLabel-shrink": {
          color: "warning.main",
        },
        "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-disabled": {
          color: "warning.main",
        },
        "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-focused": {
          color: "warning.main",
        },
        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
          borderColor: "warning.main",
        },
        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          borderColor: "warning.main",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "warning.main",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "warning.main",
        },
      }
    : undefined;
  const handleStartEditingTextValue = () => {
    onTraverseIn?.();
    if (canEditTextValue) {
      setTextEditingState({ dateRedline, isEditingTextValue: true });
    }
  };
  const shouldSkipStartEditingTextValue = () => {
    if (!skipStartEditingTextValueRef.current) return false;
    onTraverseIn?.();
    globalThis.setTimeout(() => {
      skipStartEditingTextValueRef.current = false;
    });
    return true;
  };
  const handleDisplayFieldMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest(openPickerButtonSelector)) {
      skipStartEditingTextValueRef.current = true;
    }
  };
  const handleDisplayFieldClick = (event: React.MouseEvent<HTMLDivElement>) => {
    otherProps.onClick?.(event);
    if ((event.target as HTMLElement).closest(openPickerButtonSelector) || shouldSkipStartEditingTextValue()) return;
    handleStartEditingTextValue();
  };
  const handleDisplayFieldFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    otherProps.onFocus?.(event as unknown as React.FocusEvent<HTMLDivElement>);
    if (shouldSkipStartEditingTextValue()) return;
    handleStartEditingTextValue();
  };
  const handleEditableFieldBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    otherProps.onBlur?.(event);
    if (event.currentTarget.contains(event.relatedTarget)) return;
    onManualDateAccept?.();
    setTextEditingState({ dateRedline, isEditingTextValue: false });
  };
  const handleEditableFieldKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.key === "Enter") {
      onManualDateAccept?.();
      setTextEditingState({ dateRedline, isEditingTextValue: false });
    }
  };
  const inputSlotProps: Partial<OutlinedInputProps> = {
    endAdornment: InputProps?.endAdornment ?? existingInputSlotProps.endAdornment ?? endAdornment,
    inputComponent: DiffDateInputComponent,
    inputProps: {
      ...htmlInputSlotProps,
      dateRedline,
      animateAcceptedResolved,
      negotiationId,
      onTraverseIn,
    },
    inputRef: InputProps?.inputRef,
    ref: existingInputSlotProps.ref,
    readOnly: InputProps?.readOnly,
    startAdornment: InputProps?.startAdornment ?? existingInputSlotProps.startAdornment ?? startAdornment ?? lockAdornment,
  };

  if (isEditingTextValue) {
    return (
      <PickersTextField
        {...otherProps}
        sectionListRef={sectionListRef}
        elements={elements}
        areAllSectionsEmpty={areAllSectionsEmpty}
        contentEditable={contentEditable}
        tabIndex={tabIndex}
        value={value}
        onChange={onChange}
        onInput={onInput}
        onKeyDown={handleEditableFieldKeyDown}
        onKeyUp={onKeyUp}
        onPaste={onPaste}
        onBlur={handleEditableFieldBlur}
        label={dateRedline.label}
        helperText={resolvedHelperText}
        focused={focused}
        sx={lockedFieldSx}
        slotProps={{
          ...existingSlotProps,
          htmlInput: htmlInputSlotProps,
          inputLabel: {
            ...inputLabelSlotProps,
            shrink: true,
          },
          formHelperText: formHelperTextSlotProps,
        }}
      />
    );
  }

  return (
    <>
      <PickersTextField
        {...otherProps}
        sectionListRef={sectionListRef}
        elements={elements}
        areAllSectionsEmpty={areAllSectionsEmpty}
        contentEditable={contentEditable}
        tabIndex={tabIndex}
        value={value}
        onChange={onChange}
        onInput={onInput}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onPaste={onPaste}
        label={undefined}
        helperText={undefined}
        sx={{ height: 0, opacity: 0, overflow: "hidden", pointerEvents: "none", position: "absolute", width: 0 }}
      />
      <TextField
        {...(otherProps as Omit<OutlinedTextFieldProps, "value">)}
        label={dateRedline.label}
        helperText={resolvedHelperText}
        focused={focused}
        onMouseDown={handleDisplayFieldMouseDown}
        onClick={handleDisplayFieldClick}
        onFocus={handleDisplayFieldFocus}
        sx={{
          ...(hasAcceptedResolvedChange && {
            "& .MuiInputLabel-root": {
              color: green[700],
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: green[700],
            },
            "& .MuiOutlinedInput-root": {
              backgroundColor: alpha(green[500], 0.06),
            },
            "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
              borderColor: green[500],
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: green[600],
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: green[700],
            },
            "& .MuiFormHelperText-root": {
              color: green[700],
            },
          }),
          ...lockedFieldSx,
        }}
        slotProps={{
          root: existingSlotProps?.root,
          htmlInput: htmlInputSlotProps,
          formHelperText: formHelperTextSlotProps,
          input: {
            ...inputSlotProps,
          },
          inputLabel: {
            ...inputLabelSlotProps,
            shrink: shouldShrinkLabel,
          },
        }}
      />
    </>
  );
};

interface DiffDateInputComponentProps extends InputBaseComponentProps {
  dateRedline: FieldRedline<AHDate>;
  animateAcceptedResolved?: boolean;
  negotiationId?: string;
  onTraverseIn?: () => void;
}

const DiffDateInputComponent = forwardRef<HTMLInputElement, DiffDateInputComponentProps>(
  function DiffDateInputComponent(props, forwardedRef) {
    const { className, onTraverseIn, dateRedline, animateAcceptedResolved, negotiationId, ...otherProps } = props;
    const hasAcceptedResolvedChange = dateRedline.isResolved && dateRedline.isAccepted && dateRedline.wasRevised;
    const isAcceptedResolvedRemoval = hasAcceptedResolvedChange && dateRedline.currentEntry === null;
    const showAcceptedResolvedAnimation = Boolean(
      animateAcceptedResolved &&
      hasAcceptedResolvedChange &&
      !isAcceptedResolvedRemoval
    );
    const showResolvedDiff = !hasAcceptedResolvedChange || showAcceptedResolvedAnimation || isAcceptedResolvedRemoval;

    const getTextChanges = useCallback(() => {
      const collection = new TextChangeCollection();
      let previous = "";
      let next: string;
      if (isAcceptedResolvedRemoval) {
        previous = dateRedline.originalEntry?.asFormattedString ?? dateRedline.revisedEntry?.asFormattedString ?? "";
        next = "";
      } else if (dateRedline.currentEntry === undefined) {
        previous = dateRedline.originalEntry?.asFormattedString ?? "";
        next = dateRedline.revisedEntry?.asFormattedString ?? "";
      } else {
        previous = dateRedline.revisedEntry?.asFormattedString ?? "";
        next = dateRedline.currentEntry?.asFormattedString ?? "";
      }
      const diff = dateRedline.diffWords ? diffWords(previous, next) : diffLines(previous, next);
      return diff.map((change) => new TextChange({ diff: change, parent: collection }));
    }, [dateRedline, isAcceptedResolvedRemoval]);

    return (
      <DiffTextContainer>
        <ProposalIncomingChangeHighlight field={dateRedline.field} negotiationId={negotiationId} />
        <DiffInputContainer className={className}>
          <DiffInputSpan>
            {showResolvedDiff ? (
              <DiffText
                textChanges={getTextChanges()}
                allResolved={dateRedline.isResolved}
                acceptedResolved={dateRedline.isAccepted && dateRedline.wasRevised}
                animateAcceptedResolved={animateAcceptedResolved}
                onClick={onTraverseIn}
              />
            ) : (
              <ResolvedDateValue onClick={onTraverseIn}>
                {dateRedline.currentEntry?.asFormattedString ?? dateRedline.revisedEntry?.asFormattedString ?? dateRedline.originalEntry?.asFormattedString ?? ""}
              </ResolvedDateValue>
            )}
          </DiffInputSpan>
        </DiffInputContainer>
        <Input {...otherProps} inputRef={forwardedRef} style={{ position: "absolute", height: 0, width: 0 }} />
      </DiffTextContainer>
    );
  },
);

export interface RedlineDateInputProps extends DatePickerProps {
  dateRedline: FieldRedline<AHDate>;
  issues?: ProposalIssue[];
  focused: boolean;
  hideUndoButton?: boolean;
  lockAdornment?: React.ReactNode;
  negotiationId?: string;
  viewerScope?: string;
  /** See `RedlineTextInputProps.hideHistoryBadge` (text-redline-input.tsx) — same suppression, here for the embedded `ProposalFieldHistoryButton` in the date field's adornment. */
  hideHistoryBadge?: boolean;
  onDateRedlineChange: (newRedline: FieldRedline<AHDate>, traversalFieldOverride?: ProposalField) => void;
  onTraverseIn?: () => void;
}

interface RedlineOpenPickerButtonProps extends IconButtonProps {
  dateRedline?: FieldRedline<AHDate>;
  disabled?: boolean;
  hideUndoButton?: boolean;
  negotiationId?: string;
  viewerScope?: string;
  hideHistoryBadge?: boolean;
  onAcceptChange?: () => void;
  onRejectChange?: () => void;
  onRemoveChange?: () => void;
  onUndoChanges?: () => void;
  onTraverseIn?: () => void;
}

function RedlineOpenPickerButton(props: Readonly<RedlineOpenPickerButtonProps>) {
  const {
    dateRedline,
    disabled,
    hideUndoButton,
    negotiationId,
    viewerScope,
    hideHistoryBadge,
    onAcceptChange,
    onRejectChange,
    onRemoveChange,
    onUndoChanges,
    onTraverseIn,
    ...buttonProps
  } = props;

  if (dateRedline === undefined) {
    return <ActionButton {...buttonProps} />;
  }

  return (
    <>
      {!disabled && (
        <>
          {!dateRedline.isResolved && (
            <>
              <Tooltip title="Accept Change">
                <span>
                  <ActionButton onClick={onAcceptChange}>
                    <CheckIcon color="success" />
                  </ActionButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject Change">
                <span>
                  <ActionButton onClick={onRejectChange}>
                    <CloseIcon color="error" />
                  </ActionButton>
                </span>
              </Tooltip>
            </>
          )}
          {dateRedline.isResolved && dateRedline.currentEntry && (
            <Tooltip title="Remove">
              <span>
                <ActionButton onClick={onRemoveChange}>
                  <CloseIcon />
                </ActionButton>
              </span>
            </Tooltip>
          )}
          {dateRedline.canBeUndone && !hideUndoButton && (
            <Tooltip title="Undo Changes">
              <span>
                <ActionButton onClick={onUndoChanges}>
                  <UndoIcon color="secondary" />
                </ActionButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <span>
              <ActionButton
                {...buttonProps}
                data-redline-open-picker-button="true"
                onClick={(event) => {
                  onTraverseIn?.();
                  buttonProps.onClick?.(event);
                }}
              />
            </span>
          </Tooltip>
        </>
      )}
      <ProposalFieldHistoryButton
        field={dateRedline.field}
        negotiationId={negotiationId}
        viewerScope={viewerScope}
        hideBadge={hideHistoryBadge}
      />
    </>
  );
}

const DateRedlinePicker = (props: Readonly<RedlineDateInputProps>) => {
  const {
    dateRedline,
    focused,
    disabled,
    hideUndoButton,
    lockAdornment,
    negotiationId,
    viewerScope,
    hideHistoryBadge,
    onDateRedlineChange,
    onTraverseIn,
    ...otherProps
  } = props;
  const [animateAcceptedResolved, setAnimateAcceptedResolved] = React.useState(false);
  const [draftDateState, setDraftDateState] = React.useState<{
    dateRedline: FieldRedline<AHDate>;
    draftDate: PickerValue | null;
  }>({ dateRedline, draftDate: null });
  const acceptedResolvedAnimationTimeoutRef = React.useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(undefined);
  const draftDate = draftDateState.dateRedline === dateRedline ? draftDateState.draftDate : null;

  useEffect(() => {
    return () => {
      if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
        globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
      }
    };
  }, []);

  const triggerAcceptedResolvedAnimation = useCallback(() => {
    setAnimateAcceptedResolved(true);
    if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
      globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
    }
    acceptedResolvedAnimationTimeoutRef.current = globalThis.setTimeout(() => {
      setAnimateAcceptedResolved(false);
      acceptedResolvedAnimationTimeoutRef.current = undefined;
    }, 1000);
  }, []);

  function handleChangeAccepted() {
    try {
      triggerAcceptedResolvedAnimation();
      onDateRedlineChange(dateRedline.accept());
    } catch (error) {
      console.error("Error accepting change.", error);
      enqueueSnackbar("Error accepting change.", { variant: "error" });
    }
  }

  function handleChangeRejected() {
    try {
      onDateRedlineChange(dateRedline.reject());
    } catch (error) {
      console.error("Error rejecting change.", error);
      enqueueSnackbar("Error rejecting change.", { variant: "error" });
    }
  }

  function handleChangeEdited(newDate: AHDate | null) {
    try {
      if (!dateRedline.currentEntry && !dateRedline.revisedEntry && newDate) {
        onDateRedlineChange(dateRedline.add(newDate), dateRedline.field);
        return;
      }
      onDateRedlineChange(dateRedline.edit(newDate), dateRedline.field);
    } catch (error) {
      console.error("Error editing change.", error);
      enqueueSnackbar("Error editing change.", { variant: "error" });
    }
  }

  function getDateFromPickerValue(date: PickerValue): AHDate | null {
    if (!date) return null;
    return AHDate.fromMoment(date.clone().endOf("day"));
  }

  function handleManualDateAccept() {
    if (!draftDate?.isValid()) return;
    handleChangeEdited(getDateFromPickerValue(draftDate));
  }

  function handleRemoveClicked() {
    try {
      onDateRedlineChange(dateRedline.remove(), dateRedline.field);
    } catch (error) {
      console.error("Error removing change.", error);
      enqueueSnackbar("Error removing change.", { variant: "error" });
    }
  }

  function handleUndoChanges() {
    try {
      onDateRedlineChange(dateRedline.undo(), dateRedline.field);
    } catch (error) {
      console.error("Error undoing changes.", error);
      enqueueSnackbar("Error undoing changes.", { variant: "error" });
    }
  }

  return (
    <RedlineDatePicker
      {...otherProps}
      disablePast
      disabled={disabled}
      value={draftDate ?? getDisplayedDateValue(dateRedline) ?? null}
      traversable={Boolean(onTraverseIn)}
      referenceDate={getDisplayedDateValue(dateRedline)}
      onChange={(date: PickerValue, context) => {
        setDraftDateState({ dateRedline, draftDate: date });
        if (!shouldCommitPickerDateChange(context)) return;
        handleChangeEdited(getDateFromPickerValue(date));
      }}
      slots={{
        openPickerIcon: EditCalendarIcon,
        openPickerButton: RedlineOpenPickerButton,
        day: RevisedDay,
        textField: DatePickerTextField,
      }}
      slotProps={{
        openPickerButton: {
          dateRedline,
          disabled,
          hideUndoButton,
          negotiationId,
          viewerScope,
          hideHistoryBadge,
          onAcceptChange: handleChangeAccepted,
          onRejectChange: handleChangeRejected,
          onRemoveChange: handleRemoveClicked,
          onUndoChanges: handleUndoChanges,
          onTraverseIn,
        } as RedlineOpenPickerButtonProps,
        openPickerIcon: {
          color: disabled ? "disabled" : "primary",
        },
        day: {
          originalDate: dateRedline.originalEntry,
          revisedDate: dateRedline.revisedEntry,
          currentDate: dateRedline.currentEntry,
          resolved: dateRedline.isResolved,
          acceptedResolved: dateRedline.isAccepted && dateRedline.wasRevised,
        } as RedlinePickerDayProps,
        textField: {
          error: Boolean(props.issues?.length),
          helperText: props.issues?.map((issue) => issue.description).join("\n"),
          variant: "outlined",
          margin: "normal",
          dateRedline: dateRedline,
          focused: focused,
          animateAcceptedResolved,
          lockAdornment,
          negotiationId,
          onManualDateAccept: handleManualDateAccept,
          slotProps: {
            inputLabel: {
              shrink:
                (dateRedline.currentEntry !== undefined &&
                dateRedline.currentEntry !== null) ||
                dateRedline.originalEntry !== null ||
                dateRedline.revisedEntry !== null,
            },
          },
          onFocus: onTraverseIn,
          onClick: onTraverseIn,
        } as unknown as DatePickerTextFieldProps,
      }}
    />
  );
};

export default DateRedlinePicker;
